import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { parseApiError } from '@/common/utils';
import {
  completeWhatsAppEmbeddedSignup,
  getWhatsAppEmbeddedSignupConfig,
} from '../api';
import { initFacebookSdk, loadFacebookSdk } from '../lib/loadFacebookSdk';

const COMPLETE_WAIT_MS = 20000;
const COMPLETE_POLL_MS = 100;

function isPinRequiredError(err) {
  const detail = String(
    err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.response?.data?.pin ||
      '',
  ).toLowerCase();
  return (
    err?.response?.status === 400 &&
    (detail.includes('pin') || detail.includes('2fa'))
  );
}

function getErrorMessage(err, fallback) {
  const { message } = parseApiError(err);
  return message || fallback;
}

function parseMessagePayload(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    return null;
  }

  try {
    const once = JSON.parse(trimmed);
    // Some Meta embeds double-encode JSON
    if (typeof once === 'string') {
      try {
        return JSON.parse(once);
      } catch {
        return null;
      }
    }
    return once;
  } catch {
    return null;
  }
}

function extractSessionFields(payload) {
  if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
    return null;
  }

  const data =
    typeof payload.data === 'string'
      ? parseMessagePayload(payload.data) || {}
      : payload.data && typeof payload.data === 'object'
        ? payload.data
        : {};

  return {
    event: payload.event || '',
    phone_number_id:
      data.phone_number_id ||
      data.phoneNumberId ||
      payload.phone_number_id ||
      payload.phoneNumberId ||
      '',
    waba_id:
      data.waba_id || data.wabaId || payload.waba_id || payload.wabaId || '',
    business_id:
      data.business_id ||
      data.businessId ||
      payload.business_id ||
      payload.businessId ||
      '',
  };
}

function mergeSession(prev, next) {
  if (!next) return prev;
  if (next.event === 'CANCEL') {
    return {
      event: 'CANCEL',
      phone_number_id: '',
      waba_id: '',
      business_id: '',
    };
  }

  return {
    event: next.event || prev?.event || '',
    phone_number_id: next.phone_number_id || prev?.phone_number_id || '',
    waba_id: next.waba_id || prev?.waba_id || '',
    business_id: next.business_id || prev?.business_id || '',
  };
}

function hasSessionIds(session) {
  return Boolean(session?.phone_number_id && session?.waba_id);
}

/**
 * Wait until oauth code + session IDs are both present (either arrival order).
 * oauth_code TTL is ~30s — keep wait under that.
 */
function waitForCodeAndSession(
  stateRef,
  { timeoutMs = COMPLETE_WAIT_MS } = {},
) {
  return new Promise((resolve) => {
    const started = Date.now();

    const tick = () => {
      const { oauthCode, session } = stateRef.current;

      if (session?.event === 'CANCEL' && !oauthCode) {
        resolve({ status: 'cancelled', oauthCode: null, session });
        return;
      }

      if (oauthCode && hasSessionIds(session)) {
        resolve({ status: 'ready', oauthCode, session });
        return;
      }

      if (Date.now() - started >= timeoutMs) {
        resolve({
          status: 'timeout',
          oauthCode: oauthCode || null,
          session: session || null,
        });
        return;
      }

      window.setTimeout(tick, COMPLETE_POLL_MS);
    };

    tick();
  });
}

/**
 * Meta WhatsApp Embedded Signup v4: config → FB SDK → session + code → complete.
 */
export function useWhatsAppEmbeddedSignup({ companyId, pin = '' }) {
  const queryClient = useQueryClient();
  const pinRef = useRef(pin);
  const connectingRef = useRef(false);
  const completingRef = useRef(false);

  // Shared mutable state so message listener + FB.login callback can rendezvous
  const flowRef = useRef({
    oauthCode: null,
    session: null,
  });

  const [status, setStatus] = useState('idle'); // idle | connecting | connected | error
  const [errorMessage, setErrorMessage] = useState('');
  const [pinRequired, setPinRequired] = useState(false);
  const [connection, setConnection] = useState(null);
  const [isSdkReady, setIsSdkReady] = useState(false);

  pinRef.current = pin;

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ['whatsapp-embedded-signup-config', companyId],
    queryFn: () => getWhatsAppEmbeddedSignupConfig(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Register WA_EMBEDDED_SIGNUP listener as early as possible (before FB.login)
  useEffect(() => {
    const onMessage = (event) => {
      if (
        typeof event.origin !== 'string' ||
        !event.origin.endsWith('facebook.com')
      ) {
        return;
      }

      const payload = parseMessagePayload(event.data);
      const extracted = extractSessionFields(payload);
      if (!extracted) return;

      if (import.meta.env.DEV) {
        console.log('[WA Embedded Signup] session message:', extracted);
      }

      flowRef.current.session = mergeSession(
        flowRef.current.session,
        extracted,
      );
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Load + init FB SDK once config is available
  useEffect(() => {
    if (!config?.app_id || !config?.graph_version) {
      return undefined;
    }

    let cancelled = false;
    setIsSdkReady(false);

    (async () => {
      try {
        await loadFacebookSdk();
        if (cancelled) return;
        initFacebookSdk(config);
        setIsSdkReady(true);
      } catch (err) {
        if (cancelled) return;
        setIsSdkReady(false);
        setStatus('error');
        setErrorMessage(
          err?.message || 'Failed to initialize Meta login. Please try again.',
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config]);

  useEffect(() => {
    if (isConfigError) {
      setStatus('error');
      setErrorMessage(
        getErrorMessage(
          configError,
          'Failed to load WhatsApp signup configuration.',
        ),
      );
    }
  }, [isConfigError, configError]);

  const resetError = useCallback(() => {
    setErrorMessage('');
    setPinRequired(false);
    if (status === 'error') {
      setStatus('idle');
    }
  }, [status]);

  const runComplete = useCallback(
    async (oauthCode, session) => {
      if (completingRef.current) return;
      completingRef.current = true;

      try {
        const pinValue = String(pinRef.current || '').trim();
        const body = {
          oauth_code: oauthCode,
          phone_number_id: session.phone_number_id,
          waba_id: session.waba_id,
          session_event: session.event || '',
          display_phone_number: '',
          business_name: '',
          contact_phone: '',
        };

        if (session.business_id) {
          body.business_id = session.business_id;
        }
        if (pinValue) {
          body.pin = pinValue;
        }

        if (import.meta.env.DEV) {
          console.log('[WA Embedded Signup] POST complete', {
            phone_number_id: body.phone_number_id,
            waba_id: body.waba_id,
            session_event: body.session_event,
            has_code: Boolean(body.oauth_code),
            has_pin: Boolean(body.pin),
          });
        }

        const result = await completeWhatsAppEmbeddedSignup(companyId, body);

        setConnection(result);
        setStatus('connected');
        toast.success(
          result?.registration_action === 'newly_registered'
            ? 'WhatsApp connected and number registered'
            : 'WhatsApp connected',
        );

        queryClient.invalidateQueries({
          queryKey: ['company-integrations', companyId],
        });
      } catch (err) {
        if (isPinRequiredError(err) && !String(pinRef.current || '').trim()) {
          setPinRequired(true);
          setStatus('error');
          setErrorMessage(
            'Enter your 6-digit WhatsApp 2FA PIN, then reconnect.',
          );
          return;
        }

        setStatus('error');
        setErrorMessage(
          getErrorMessage(
            err,
            'Failed to complete WhatsApp connection. Please try again.',
          ),
        );
      } finally {
        completingRef.current = false;
        connectingRef.current = false;
      }
    },
    [companyId, queryClient],
  );

  const launchSignup = useCallback(() => {
    if (connectingRef.current) return;
    if (!companyId) {
      setStatus('error');
      setErrorMessage(
        'Company details not found. Please register your company first.',
      );
      return;
    }
    if (!config?.fb_login) {
      setStatus('error');
      setErrorMessage(
        'WhatsApp signup is not ready yet. Please wait or retry.',
      );
      return;
    }
    if (!isSdkReady || !window.FB) {
      setStatus('error');
      setErrorMessage(
        'Meta login is still loading. Please wait a moment and try again.',
      );
      return;
    }

    connectingRef.current = true;
    completingRef.current = false;
    setStatus('connecting');
    setErrorMessage('');
    setPinRequired(false);

    // Reset only for this attempt (listener stays registered)
    flowRef.current = { oauthCode: null, session: null };

    const fbLogin = config.fb_login;

    // FB.login requires a plain function — not async
    const handleLoginResponse = (response) => {
      if (import.meta.env.DEV) {
        console.log('[WA Embedded Signup] FB.login response', {
          hasCode: Boolean(response?.authResponse?.code),
          status: response?.status,
          session: flowRef.current.session,
        });
      }

      const oauthCode = response?.authResponse?.code || null;
      if (oauthCode) {
        flowRef.current.oauthCode = oauthCode;
      }

      void (async () => {
        try {
          const result = await waitForCodeAndSession(flowRef);

          if (result.status === 'cancelled') {
            toast('WhatsApp connection cancelled.', { icon: 'ℹ️' });
            setStatus('idle');
            connectingRef.current = false;
            return;
          }

          if (result.status === 'ready') {
            await runComplete(result.oauthCode, result.session);
            return;
          }

          // timeout
          if (!result.oauthCode && !hasSessionIds(result.session)) {
            toast('WhatsApp connection was closed before completing.', {
              icon: 'ℹ️',
            });
            setStatus('idle');
            connectingRef.current = false;
            return;
          }

          if (result.oauthCode && !hasSessionIds(result.session)) {
            setStatus('error');
            setErrorMessage(
              'Meta returned a login code but phone/WABA session details were missing. Keep the Meta window open until Finish, then try again.',
            );
            connectingRef.current = false;
            return;
          }

          setStatus('error');
          setErrorMessage(
            'Meta signup finished without a login code. Please try connecting again.',
          );
          connectingRef.current = false;
        } catch (err) {
          connectingRef.current = false;
          setStatus('error');
          setErrorMessage(
            err?.message ||
              'Failed to complete WhatsApp connection. Please try again.',
          );
        }
      })();
    };

    try {
      window.FB.login(handleLoginResponse, {
        config_id: fbLogin.config_id,
        response_type: fbLogin.response_type || 'code',
        override_default_response_type:
          fbLogin.override_default_response_type ?? true,
        extras: fbLogin.extras,
      });
    } catch (err) {
      connectingRef.current = false;
      setStatus('error');
      setErrorMessage(
        err?.message || 'Failed to open Meta login. Please try again.',
      );
    }
  }, [companyId, config, isSdkReady, runComplete]);

  return {
    status,
    connection,
    errorMessage,
    pinRequired,
    config,
    isConfigLoading,
    isConfigError,
    isReady: !!config?.fb_login && isSdkReady,
    isConnecting: status === 'connecting',
    isConnected: status === 'connected',
    launchSignup,
    resetError,
    refetchConfig,
  };
}
